/* eslint-disable @typescript-eslint/naming-convention -- campos em snake_case espelham o contrato da API */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildHttpParams } from '../../../core/utils/http-util';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../core/constants/endpoints';
import {
  IConversationHistoryView,
  IConversationHistoryViewRaw,
  toConversationHistoryView
} from '../interfaces/conversation-history-view.interface';
import { toConversationCreateRequest } from '../interfaces/conversation-create-request.interface';
import {
  IConversationView,
  IConversationViewRaw,
  toConversationView
} from '../interfaces/conversation-view.interface';
import { IHITLResponse, ISessionState } from '../interfaces/hitl.interface';
import { IStopRunRequest, IStopRunResponse } from '../interfaces/stop-run.interface';
import {
  IConversationListing,
  IListConversationsOutputRaw
} from '../interfaces/list-conversations-output.interface';
import { EnumContexto } from '../interfaces/topic.interface';
import { toSetPinnedRequest } from '../interfaces/set-pinned-request.interface';
import {
  AYA_STREAM_PROTOCOL,
  IStreamMessageRequest,
  toStreamMessageRequest
} from '../interfaces/stream-message-request.interface';
import {
  IAyaStreamEvent,
  ServerSentEventsService
} from '../services/server-sent-events.service';

const CONVERSATIONS_PAGE_SIZE = 100;

@Injectable({
  providedIn: 'root'
})
export class ChatApi {
  constructor(
    private http: HttpClient,
    private sseService: ServerSentEventsService
  ) {}

  createConversation(contexto?: EnumContexto): Observable<IConversationView> {
    return this.http
      .post<IConversationViewRaw>(
        `${environment.ceppemAiApi}/${endpoints.ceppemAi.conversations}`,
        toConversationCreateRequest(contexto)
      )
      .pipe(map(toConversationView));
  }

  streamMessage(sessionId: string, request: IStreamMessageRequest): Observable<IAyaStreamEvent> {
    const url = `${environment.ceppemAiApi}/${endpoints.ceppemAi.streamChat}/${sessionId}`;
    return this.sseService.connectStream(url, sessionId, toStreamMessageRequest(request));
  }

  /** Reconexão é sempre por aqui: repetir o POST de `streamMessage` inicia outra execução. */
  joinRun(sessionId: string, runId: string, cursor?: string | null): Observable<IAyaStreamEvent> {
    const url =
      `${environment.ceppemAiApi}/${endpoints.ceppemAi.streamChat}/${sessionId}` +
      `/runs/${runId}?stream_protocol=${AYA_STREAM_PROTOCOL}`;
    return this.sseService.connectStream(url, sessionId, null, cursor, runId);
  }

  /** O `run_id` no body impede que uma aba antiga cancele a execução seguinte. */
  stopRun(sessionId: string, runId: string): Observable<IStopRunResponse> {
    const body: IStopRunRequest = { run_id: runId };
    return this.http.post<IStopRunResponse>(
      `${environment.ceppemAiApi}/${endpoints.ceppemAi.streamChat}/${sessionId}/stop`,
      body
    );
  }

  resume(sessionId: string, body: IHITLResponse): Observable<IAyaStreamEvent> {
    const url = `${environment.ceppemAiApi}/${endpoints.ceppemAi.session}/${sessionId}/resume`;
    return this.sseService.connectStream(url, sessionId, body);
  }

  getSessionState(sessionId: string): Observable<ISessionState> {
    return this.http.get<ISessionState>(
      `${environment.ceppemAiApi}/${endpoints.ceppemAi.session}/${sessionId}/state`
    );
  }

  listConversations(): Observable<IConversationListing> {
    const params = buildHttpParams({
      page: 1,
      ['page_size']: CONVERSATIONS_PAGE_SIZE
    });
    return this.http
      .get<IListConversationsOutputRaw>(
        `${environment.ceppemAiApi}/${endpoints.ceppemAi.conversations}`,
        { params }
      )
      .pipe(
        map(res => ({
          items: res.items.map(toConversationView),
          userStreamCursor: res.user_stream_cursor ?? null
        }))
      );
  }

  fetchConversationHistory(conversationId: string): Observable<IConversationHistoryView> {
    return this.http
      .get<IConversationHistoryViewRaw>(
        `${environment.ceppemAiApi}/${endpoints.ceppemAi.conversations}/${conversationId}/messages`
      )
      .pipe(map(toConversationHistoryView));
  }

  deleteConversation(conversationId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.ceppemAiApi}/${endpoints.ceppemAi.conversations}/${conversationId}`
    );
  }

  setPinned(conversationId: string, pinned: boolean): Observable<IConversationView> {
    return this.http
      .patch<IConversationViewRaw>(
        `${environment.ceppemAiApi}/${endpoints.ceppemAi.conversations}/${conversationId}`,
        toSetPinnedRequest(pinned)
      )
      .pipe(map(toConversationView));
  }

  renameConversation(conversationId: string, title: string): Observable<IConversationView> {
    return this.http
      .patch<IConversationViewRaw>(
        `${environment.ceppemAiApi}/${endpoints.ceppemAi.conversations}/${conversationId}`,
        { title }
      )
      .pipe(map(toConversationView));
  }

  downloadFile(objectPath: string): Observable<Blob> {
    const encoded = objectPath.split('/').map(encodeURIComponent).join('/');
    return this.http.get(
      `${environment.ceppemAiApi}/${endpoints.ceppemAi.files}/${encoded}`,
      { responseType: 'blob' }
    );
  }
}
